import { format, formatInTimeZone } from 'date-fns-tz';
import { IANA_TIME_ZONE_MAP } from '../../util/constants';
import { DEFAULT_FACILITY_TIME_ZONE } from '../../util/encounterNumberTemplate';

export const EXTRACTION_ENCOUNTER_NUMBER_TEMPLATE_MODIFIERS = ['initial', 'yyyyMMdd', 'HHmmss'] as const;
export const EXTRACTION_ENCOUNTER_NUMBER_TEMPLATE_NOW_CONSTANT = '@now';

export type ExtractionEncounterNumberTemplateModifier =
    (typeof EXTRACTION_ENCOUNTER_NUMBER_TEMPLATE_MODIFIERS)[number];

export interface ExtractionEncounterNumberTemplateToken {
    rawToken: string;
    fieldName: string;
    modifier: ExtractionEncounterNumberTemplateModifier | null;
    specialToken: boolean;
    constantName: string | null;
}

export interface ExtractionEncounterNumberTemplateValidationResult {
    valid: boolean;
    errors: string[];
    normalizedTemplate: string | null;
}

const TOKEN_OPEN = '{{';
const TOKEN_CLOSE = '}}';

function isRecord(value: unknown): value is Record<string, any> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function normalizeStringValue(value: unknown): string | null {
    if (value === null || value === undefined) {
        return null;
    }

    const normalized = String(value).trim();
    return normalized.length > 0 ? normalized : null;
}

function extractInitial(value: unknown): string | null {
    const normalized = normalizeStringValue(value);
    return normalized ? normalized.charAt(0) : null;
}

function isValidTimeParts(hour: string, minute: string, second: string): boolean {
    const hourValue = Number(hour);
    const minuteValue = Number(minute);
    const secondValue = Number(second);

    return (
        Number.isInteger(hourValue) &&
        Number.isInteger(minuteValue) &&
        Number.isInteger(secondValue) &&
        hourValue >= 0 &&
        hourValue <= 23 &&
        minuteValue >= 0 &&
        minuteValue <= 59 &&
        secondValue >= 0 &&
        secondValue <= 59
    );
}

function isValidDateParts(year: number, month: number, day: number): boolean {
    const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const maxDaysByMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return (
        Number.isInteger(year) &&
        Number.isInteger(month) &&
        Number.isInteger(day) &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= maxDaysByMonth[month - 1]
    );
}

function isValidIanaTimeZone(timeZone: string): boolean {
    try {
        Intl.DateTimeFormat(undefined, { timeZone });
        return true;
    } catch {
        return false;
    }
}

function formatDateToYYYYMMDD(value: unknown): string | null {
    if (value === null || value === undefined) {
        return null;
    }

    if (value instanceof Date) {
        return !isNaN(value.getTime()) ? format(value, 'yyyyMMdd') : null;
    }

    const normalized = String(value).trim();
    if (!normalized) {
        return null;
    }

    const alreadyFormattedMatch = normalized.match(/^(\d{8})$/);
    if (alreadyFormattedMatch) {
        return alreadyFormattedMatch[1];
    }

    const isoDateMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s].*)/);
    if (isoDateMatch) {
        const year = Number(isoDateMatch[1]);
        const month = Number(isoDateMatch[2]);
        const day = Number(isoDateMatch[3]);
        return isValidDateParts(year, month, day) ? `${isoDateMatch[1]}${isoDateMatch[2]}${isoDateMatch[3]}` : null;
    }

    const slashDateMatch = normalized.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (slashDateMatch) {
        const firstPart = Number(slashDateMatch[1]);
        const secondPart = Number(slashDateMatch[2]);
        const year = Number(slashDateMatch[3]);
        const month = firstPart > 12 ? secondPart : firstPart;
        const day = firstPart > 12 ? firstPart : secondPart;
        if (!isValidDateParts(year, month, day)) {
            return null;
        }
        return `${year}${`${month}`.padStart(2, '0')}${`${day}`.padStart(2, '0')}`;
    }

    return null;
}

function formatTimeToHHMMSS(value: unknown): string | null {
    if (value === null || value === undefined) {
        return null;
    }

    if (value instanceof Date) {
        return !isNaN(value.getTime()) ? format(value, 'HHmmss') : null;
    }

    const normalized = String(value).trim();
    if (!normalized) {
        return null;
    }

    const compactTimeMatch = normalized.match(/^(\d{2})(\d{2})(\d{2})$/);
    if (compactTimeMatch) {
        const [, hour, minute, second] = compactTimeMatch;
        return isValidTimeParts(hour, minute, second) ? `${hour}${minute}${second}` : null;
    }

    const compactMinuteMatch = normalized.match(/^(\d{2})(\d{2})$/);
    if (compactMinuteMatch) {
        const [, hour, minute] = compactMinuteMatch;
        return isValidTimeParts(hour, minute, '00') ? `${hour}${minute}00` : null;
    }

    const colonTimeMatch = normalized.match(
        /^(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/,
    );
    if (colonTimeMatch) {
        const [, hour, minute, second = '00'] = colonTimeMatch;
        return isValidTimeParts(hour, minute, second) ? `${hour}${minute}${second}` : null;
    }

    return null;
}

function resolveIanaTimeZone(facilityTimeZone?: string): string {
    const normalizedFacilityTimeZone = normalizeStringValue(facilityTimeZone) || DEFAULT_FACILITY_TIME_ZONE;
    if (
        /^[A-Za-z_]+\/[A-Za-z0-9_+\-\/]+$/.test(normalizedFacilityTimeZone) &&
        isValidIanaTimeZone(normalizedFacilityTimeZone)
    ) {
        return normalizedFacilityTimeZone;
    }
    if (normalizedFacilityTimeZone in IANA_TIME_ZONE_MAP) {
        return IANA_TIME_ZONE_MAP[normalizedFacilityTimeZone as keyof typeof IANA_TIME_ZONE_MAP];
    }

    return IANA_TIME_ZONE_MAP[DEFAULT_FACILITY_TIME_ZONE];
}

function formatNow(
    now: Date,
    facilityTimeZone: string | undefined,
    modifier: ExtractionEncounterNumberTemplateModifier,
): string {
    const pattern = modifier === 'HHmmss' ? 'HHmmss' : 'yyyyMMdd';
    return formatInTimeZone(now, resolveIanaTimeZone(facilityTimeZone), pattern);
}

function getExtractionSchemaProperties(extractionSchema: unknown): Record<string, unknown> {
    if (!isRecord(extractionSchema) || !isRecord(extractionSchema.properties)) {
        return {};
    }
    return extractionSchema.properties;
}

export function getExtractionSchemaFieldNames(extractionSchema: unknown): string[] {
    return Object.keys(getExtractionSchemaProperties(extractionSchema));
}

function isSupportedModifier(value: string): value is ExtractionEncounterNumberTemplateModifier {
    return (EXTRACTION_ENCOUNTER_NUMBER_TEMPLATE_MODIFIERS as readonly string[]).includes(value);
}

function buildNormalizedTokenContent(token: ExtractionEncounterNumberTemplateToken): string {
    const fieldName = token.constantName || token.fieldName;
    return `${fieldName}${token.modifier ? `|${token.modifier}` : ''}`;
}

function normalizeTemplateSyntax(template: string): string {
    return template.replace(/{{([\s\S]*?)}}/g, (_match, rawToken: string) => {
        const token = parseTokenContent(rawToken);
        return `{{${buildNormalizedTokenContent(token)}}}`;
    });
}

function parseTokenContent(rawToken: string): ExtractionEncounterNumberTemplateToken {
    const content = rawToken.trim();
    if (!content || content.includes(TOKEN_OPEN) || content.includes(TOKEN_CLOSE)) {
        throw new Error('encounterNumberTemplate has malformed token syntax');
    }

    const parts = content.split('|');
    if (parts.length > 2) {
        throw new Error('encounterNumberTemplate has malformed token syntax');
    }

    const fieldName = parts[0].trim();
    const modifierName = parts[1]?.trim() || null;
    if (!fieldName || fieldName.includes('{') || fieldName.includes('}') || fieldName.includes('|')) {
        throw new Error('encounterNumberTemplate has malformed token syntax');
    }
    if (modifierName !== null && !isSupportedModifier(modifierName)) {
        throw new Error(`encounterNumberTemplate contains unsupported modifier "${modifierName}"`);
    }
    const modifier: ExtractionEncounterNumberTemplateModifier | null =
        modifierName === null ? null : (modifierName as ExtractionEncounterNumberTemplateModifier);

    if (fieldName.startsWith('@')) {
        if (fieldName !== EXTRACTION_ENCOUNTER_NUMBER_TEMPLATE_NOW_CONSTANT) {
            throw new Error(`encounterNumberTemplate contains unsupported constant "${fieldName}"`);
        }
        if (modifier !== 'yyyyMMdd' && modifier !== 'HHmmss') {
            throw new Error(
                `encounterNumberTemplate constant "${fieldName}" requires yyyyMMdd or HHmmss modifier`,
            );
        }
        return {
            rawToken,
            fieldName,
            modifier,
            specialToken: true,
            constantName: fieldName,
        };
    }

    return {
        rawToken,
        fieldName,
        modifier,
        specialToken: false,
        constantName: null,
    };
}

export function extractExtractionEncounterNumberTemplateTokens(
    template: string,
): ExtractionEncounterNumberTemplateToken[] {
    if (typeof template !== 'string') {
        throw new Error('encounterNumberTemplate must be a string');
    }

    const tokens: ExtractionEncounterNumberTemplateToken[] = [];
    let cursor = 0;

    while (cursor < template.length) {
        const nextOpen = template.indexOf(TOKEN_OPEN, cursor);
        const nextClose = template.indexOf(TOKEN_CLOSE, cursor);

        if (nextClose !== -1 && (nextOpen === -1 || nextClose < nextOpen)) {
            throw new Error('encounterNumberTemplate has malformed token braces');
        }

        if (nextOpen === -1) {
            break;
        }

        const endIndex = template.indexOf(TOKEN_CLOSE, nextOpen + TOKEN_OPEN.length);
        if (endIndex === -1) {
            throw new Error('encounterNumberTemplate has malformed token braces');
        }

        tokens.push(parseTokenContent(template.slice(nextOpen + TOKEN_OPEN.length, endIndex)));
        cursor = endIndex + TOKEN_CLOSE.length;
    }

    return tokens;
}

export function validateExtractionEncounterNumberTemplate(params: {
    template: unknown;
    extractionSchema: unknown;
}): ExtractionEncounterNumberTemplateValidationResult {
    const { template, extractionSchema } = params;
    const errors: string[] = [];

    if (template === undefined || template === null) {
        return { valid: true, errors: [], normalizedTemplate: null };
    }
    if (typeof template !== 'string') {
        return {
            valid: false,
            errors: ['encounterNumberTemplate must be a string or null'],
            normalizedTemplate: null,
        };
    }
    if (template.length > 1000) {
        errors.push('encounterNumberTemplate must be 1000 characters or less');
    }

    const normalizedTemplate = template.trim();
    if (!normalizedTemplate) {
        return {
            valid: errors.length === 0,
            errors,
            normalizedTemplate: null,
        };
    }

    let tokens: ExtractionEncounterNumberTemplateToken[] = [];
    try {
        tokens = extractExtractionEncounterNumberTemplateTokens(normalizedTemplate);
    } catch (error) {
        errors.push(error instanceof Error ? error.message : String(error));
    }

    const extractionFields = new Set(getExtractionSchemaFieldNames(extractionSchema));
    for (const token of tokens) {
        if (token.specialToken) {
            continue;
        }
        if (!extractionFields.has(token.fieldName)) {
            errors.push(`encounterNumberTemplate references unknown extraction field "${token.fieldName}"`);
        }
    }

    return {
        valid: errors.length === 0,
        errors,
        normalizedTemplate: errors.length === 0 ? normalizeTemplateSyntax(normalizedTemplate) : null,
    };
}

export function normalizeExtractionEncounterNumberTemplate(params: {
    template: unknown;
    extractionSchema: unknown;
}): string | null {
    const validation = validateExtractionEncounterNumberTemplate(params);
    if (!validation.valid) {
        throw new Error(validation.errors.join('; '));
    }
    return validation.normalizedTemplate;
}

function formatTokenValue(value: unknown, modifier: ExtractionEncounterNumberTemplateModifier | null): string | null {
    if (modifier === 'initial') {
        return extractInitial(value);
    }
    if (modifier === 'yyyyMMdd') {
        return formatDateToYYYYMMDD(value);
    }
    if (modifier === 'HHmmss') {
        return formatTimeToHHMMSS(value);
    }
    return normalizeStringValue(value);
}

export function renderExtractionEncounterNumberTemplate(params: {
    template: string;
    extractionSchema: unknown;
    extractedData: Record<string, unknown>;
    facilityTimeZone?: string;
}): {
    encounterNumber: string;
    missingFields: string[];
} {
    const { template, extractionSchema, extractedData, facilityTimeZone } = params;
    const normalizedTemplate = normalizeExtractionEncounterNumberTemplate({
        template,
        extractionSchema,
    });
    if (!normalizedTemplate) {
        return { encounterNumber: '', missingFields: [] };
    }

    const missingFields = new Set<string>();
    const now = new Date();
    const rendered = normalizedTemplate.replace(/{{([\s\S]*?)}}/g, (match, rawToken: string) => {
        const token = parseTokenContent(rawToken);
        if (token.constantName === EXTRACTION_ENCOUNTER_NUMBER_TEMPLATE_NOW_CONSTANT) {
            return formatNow(now, facilityTimeZone || DEFAULT_FACILITY_TIME_ZONE, token.modifier || 'yyyyMMdd');
        }

        const value = formatTokenValue(extractedData[token.fieldName], token.modifier);
        if (!value) {
            missingFields.add(token.fieldName);
            return '';
        }
        return value;
    });

    return {
        encounterNumber: rendered,
        missingFields: Array.from(missingFields),
    };
}

const LEGACY_ENCOUNTER_TEMPLATE_TOKEN_MAP: Record<string, string> = {
    FIRST_NAME: 'patFrstNm',
    LAST_NAME: 'patLastNm',
    FIRST_NAME_INITIAL: 'patFrstNm|initial',
    LAST_NAME_INITIAL: 'patLastNm|initial',
    DOB_YYYYMMDD: 'patDob|yyyyMMdd',
    ADMIT_DATE_YYYYMMDD: 'admitDt|yyyyMMdd',
    ADMIT_TIME_HHMMSS: 'admitTm|HHmmss',
    TODAY_DATE_YYYYMMDD: '@now|yyyyMMdd',
    ENCOUNTER_NUMBER: 'enctrNo',
    MRN: 'patMrn',
};

export function convertLegacyEncounterNumberTemplateToExtractionTemplate(template: unknown): string | null {
    if (template === undefined || template === null) {
        return null;
    }
    if (typeof template !== 'string') {
        throw new Error('encounterNumberTemplate must be a string or null');
    }

    const trimmedTemplate = template.trim();
    if (!trimmedTemplate) {
        return null;
    }

    return trimmedTemplate.replace(/{{\s*([A-Z0-9_]+)\s*}}/g, (match, tokenName: string) => {
        const replacement = LEGACY_ENCOUNTER_TEMPLATE_TOKEN_MAP[tokenName];
        if (!replacement) {
            return match;
        }
        return `{{${replacement}}}`;
    });
}
